import { autocompletion, Completion, CompletionContext } from '@codemirror/autocomplete'
import { DataObjectRounded, NotListedLocationRounded } from '@mui/icons-material'
import { Box, Stack, Tooltip, Typography } from '@mui/material'
import { memo } from 'react'
import { INodeReportPlaceholder } from '../../../../../definitions/Nodes'
import NodeCodemirrorManager from '../../../../../managers/NodeCodemirrorManager'
import { IShapeInput } from '../../../../../managers/NodeManager'
import NodeCodemirrorTextEditor from '../../NodeCodemirrorTextEditor'
import { useDebouncedCommit } from './NodeReportEditor'

interface P {
    contents: { name: string, contents: string[] }[]
    placeholder: INodeReportPlaceholder
    placeholderIdx: number
    disable: boolean
    shapes: IShapeInput[]
    onTextPlaceholderChange: (newValue: string, placeholderIdx: number) => void
    globalsSuggestions: Completion[]
}

const TextPlaceholder = ({ contents, placeholder, placeholderIdx, onTextPlaceholderChange, disable, shapes, globalsSuggestions }: P) => {
    const keywords = NodeCodemirrorManager.generateCompletionsFromShapes(shapes)
    const placeholderContents = contents?.find(c => c.name === placeholder.name)?.contents

    const commit = (val: string) => {
        const parsedValue = val.startsWith('globals.')
            ? `@{${val}}`
            : `@{${NodeCodemirrorManager.changeLabelToUuid(val, keywords)}}`
        onTextPlaceholderChange(parsedValue, placeholderIdx)
    }

    const [input, setInput] = useDebouncedCommit(
        placeholder.referenceData && placeholder.referenceData.startsWith('globals.')
            ? placeholder.referenceData.slice(2, -1)
            : placeholder.referenceData && !placeholder.referenceData.startsWith('globals.')
                ? NodeCodemirrorManager.changeLabelToUuid(
                    placeholder.referenceData.slice(2, -1),
                    keywords,
                    true
                )
                : '',
        commit,
        500
    )

    const customCompletions = (context: CompletionContext) => {
        const word = context.matchBefore(/\w*/)
        if (word.from === word.to && !context.explicit) return null
        return { from: word.from, options: [...keywords, ...globalsSuggestions] }
    }

    return (
        <Stack flexDirection='row' alignItems='center' gap={1}>
            <Stack flexDirection='row' alignItems='center' width={170} gap={1}>
                <DataObjectRounded fontSize='small' />
                <Typography noWrap variant='subtitle2'>{placeholder.name}</Typography>
            </Stack>
            <Stack flexDirection='row' flex={1} overflow='hidden'>
                <NodeCodemirrorTextEditor
                    disabled={disable}
                    value={input}
                    onChange={setInput}
                    extensions={[autocompletion({ override: [customCompletions] })]}
                />
            </Stack>
            <Tooltip
                title={
                    placeholderContents?.length > 0
                        ? placeholderContents.map((c, i) => <Box key={i}>{c}</Box>)
                        : ''
                }
                placement='left'
                arrow>
                <NotListedLocationRounded
                    fontSize='small'
                    color={placeholderContents?.length === 0 ? 'disabled' : undefined}
                />
            </Tooltip>
        </Stack>
    )
}

export default memo(TextPlaceholder)
